import {
  FormEvent, useCallback, useMemo, useState,
} from 'react';
import { generateNumbersArray } from '@utils/arrays/generateNumbersArray';

/**
 * @description Custom hook for multiple locations combobox and for selected locations pills (a pill contains the location name and a remove button).
 * It also contains the value for current radius parameter and the available radius values.
 *
 * @returns Object with the following properties:
 * - **comboboxOptions:** Combobox options object, so we don't have to pass the same options to the combobox component every time.
 * - **selectedLocations:** Array of selected locations to be displayed as pills.
 * - **selectedLocationsString:** String of selected locations to be used in a hidden input (or wherever needed) used for form posting.
 * - **selectedLocationsFiltersLabel:** String of selected locations to be used for filters.
 * - **buildSelectedLocationsFromString:** Function to build the selectedLocations array from the hidden input value. If the form is updated programmatically,
 * we need a way to update the selected locations array and properly build the location pills.
 * - **addLocation:** Function to add a selected location from the combobox to the selectedLocations array. Add it only if it's not already in the selectedLocations array.
 * - **deleteLocation:** Function to delete a location from the selectedLocations array.
 * - **radiusArray:** Array that contains the available values for the radius parameter.
 * - **range:** The currently selected range (default 30Km).
 * - **setRange:** SetStateAction, set current range value.
 *
 */
export const useLocationsBuilder = () => {
  // Combobox options
  // **********************************************
  const comboboxOptions = {
    freeSolo: false,
    valueFromKey: 'locality',
    clearOnSelect: true,
    apiList: {
      fetchUrl: '/v1/location-tooltip',
      queryParams: (query: string) => ({ location: query }),
    },
    showNoResultsMessage: true,
  };

  // Store the locations
  // **********************************************
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);


  // Radius parameter
  // **********************************************
  // Array of numbers for the radius filter
  const radiusArray = useMemo(() => generateNumbersArray(0, 100, 10), []);

  // Store the range and use it for the radius filter label
  const [range, setRange] = useState<number>(30);

  // Convert the selectedLocations array to a string to be used in the hidden input (or wherever needed)
  // **********************************************
  const selectedLocationsString = useMemo(
    () => selectedLocations.join(','),
    [selectedLocations],
  );

  // Convert the selectedLocations array to a string to be used for labels.
  // **********************************************
  const selectedLocationsFiltersLabel = useMemo(
    () => selectedLocations.join(', '),
    [selectedLocations],
  );

  // Build the selectedLocations array from the hidden input value.
  // Used when the form is updated programmatically.
  // **********************************************
  const buildSelectedLocationsFromString = useCallback((event: FormEvent<HTMLInputElement>) => {
    // Helper variables
    const inputValue = event.currentTarget.value;
    const locationsArray = inputValue.split(',');

    // Update the selected locations
    setSelectedLocations(inputValue === '' ? [] : locationsArray);
  }, []);

  // Add a location to the selectedLocations array
  // **********************************************
  const addLocation = (location: string) => {
    setSelectedLocations((prevLocations) => {
      if (!prevLocations.includes(location)) {
        return [...prevLocations, location];
      }
      return prevLocations;
    });
  };

  // Delete a location from the selectedLocations array
  // **********************************************
  const deleteLocation = (locationToDelete: string) => {
    setSelectedLocations((prevLocations) => prevLocations.filter((location) => location !== locationToDelete));
  };


  return {
    comboboxOptions,
    selectedLocations,
    selectedLocationsString,
    selectedLocationsFiltersLabel,
    buildSelectedLocationsFromString,
    addLocation,
    deleteLocation,
    radiusArray,
    range,
    setRange,
  };
};
